import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Mail } from "lucide-react";
import { joinGame, resolveWebSocketUrl } from "../../api/gameService";
import { useAuth } from "../../context/AuthContext";
import { useAvatar } from "../../context/AvatarContext";
import { useGameCtx } from "../../context/GameContext";
import { fetchTopHeaderLinks } from "../../api/siteService";
import { ensureGuestId, getGuestPseudo } from "../../utils/localStorage";
import logo from "../../assets/images/logo.png";
import Avatar from "../ui/Avatar";
import { fetchNotifications } from "../../api/notificationService";
import IdentityChoiceModal from "../modals/IdentityChoiceModal";
import QuickPlayModal, {
  saveRecentQuickPlayCategory,
} from "../modals/QuickPlayModal";
import "../../assets/css/Header.css";

const NAV_ITEMS_BEFORE_COMMUNITY = [
  { label: "Accueil", href: "/" },
  { label: "Classements", href: "/classement" },
  { label: "Forum", href: "/forum" },
];

const NAV_ITEMS_AFTER_COMMUNITY = [
  { label: "FAQ", href: "/faq" },
];

const DEFAULT_TOP_HEADER_LINKS = [
  {
    id: "contact",
    label: "Contactez-nous",
    icon: "contact",
    icon_key: "contact",
    url: "/contact",
    is_active: true,
    is_external: false,
    display_order: 1,
  },
];

function SocialIconBadge({ iconKey }) {
  if (iconKey === "contact") {
    return <Mail className="h-3.5 w-3.5" />;
  }
  if (iconKey === "facebook") {
    return (
      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
      </svg>
    );
  }
  if (iconKey === "discord") {
    return (
      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.043.03.055a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
      </svg>
    );
  }
  if (iconKey === "instagram") {
    return (
      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
      </svg>
    );
  }
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

function isExternalLink(url) {
  return /^https?:\/\//i.test(url || "");
}

function Header() {
  const [profileOpen, setProfileOpen] = useState(false);
  const [isQuickPlayOpen, setIsQuickPlayOpen] = useState(false);
  const [isIdentityModalOpen, setIsIdentityModalOpen] = useState(false);
  const [pendingQuickCategory, setPendingQuickCategory] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [topHeaderLinks, setTopHeaderLinks] = useState([]);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, canAccessMemberArea, logout, user } = useAuth();
  const { currentAvatar } = useAvatar();
  const { ensureAnon, player, setPlayer, setSelectedCategory } = useGameCtx();
  const authenticatedUserId = user?.user_id || user?.id || null;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleOpenQuickPlay = () => {
      setIsQuickPlayOpen(true);
      setIsMobileMenuOpen(false);
    };

    window.addEventListener("openQuickPlay", handleOpenQuickPlay);
    return () => window.removeEventListener("openQuickPlay", handleOpenQuickPlay);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setProfileOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!isAuthenticated || !canAccessMemberArea) {
      setUnreadNotifications(0);
      return undefined;
    }

    const controller = new AbortController();
    fetchNotifications(controller.signal)
      .then((payload) => {
        setUnreadNotifications(Number(payload?.unread_count || 0));
      })
      .catch(() => {
        setUnreadNotifications(0);
      });

    return () => controller.abort();
  }, [isAuthenticated, canAccessMemberArea, location.pathname]);

  useEffect(() => {
    const controller = new AbortController();
    fetchTopHeaderLinks(controller.signal)
      .then((payload) => {
        const links = Array.isArray(payload?.links) ? payload.links : [];
        setTopHeaderLinks(links.length > 0 ? links : DEFAULT_TOP_HEADER_LINKS);
      })
      .catch(() => setTopHeaderLinks(DEFAULT_TOP_HEADER_LINKS));

    return () => controller.abort();
  }, []);

  const handleAuthClick = (target) => {
    setIsMobileMenuOpen(false);

    if (location.pathname === "/authentification") {
      window.dispatchEvent(new CustomEvent("switchAuthForm", { detail: target }));
    } else {
      navigate(`/authentification#${target}`);
    }
  };

  const handleLogout = () => {
    logout();
    setProfileOpen(false);
    navigate("/");
  };

  const launchCategory = async (category, forcedAnon = null) => {
    if (!category?.slug) {
      return;
    }

    const activeAnon = forcedAnon || null;

    const data = await joinGame(category.slug);

    if (!data?.ws_url) {
      throw new Error("Aucune URL WebSocket recue.");
    }

    const finalWsUrl = resolveWebSocketUrl(data.ws_url);

    saveRecentQuickPlayCategory(category);
    setSelectedCategory({ slug: category.slug, name: category.name });
    setIsQuickPlayOpen(false);
    setPendingQuickCategory(null);

    navigate(`/game-quiz/${category.slug}`, {
      state: {
        wsUrl: finalWsUrl,
        category,
        player: {
          player_id: isAuthenticated
            ? authenticatedUserId
            : activeAnon?.uuid || player?.id || ensureGuestId(),
          pseudo: isAuthenticated
            ? user?.username
            : activeAnon?.pseudo || player?.pseudo || getGuestPseudo() || "Invite",
          avatar_id: currentAvatar?.id || null,
          guest_id: isAuthenticated
            ? null
            : activeAnon?.guest_id || player?.guest_id || ensureGuestId(),
        },
      },
    });
  };

  const handleQuickPlaySelect = async (category) => {
    try {
      if (isAuthenticated) {
        await launchCategory(category);
        return;
      }

      setSelectedCategory({ slug: category.slug, name: category.name });
      setPendingQuickCategory(category);
      setIsQuickPlayOpen(false);
      setIsIdentityModalOpen(true);
    } catch (error) {
      console.error(error);
      window.alert("Impossible de lancer cette categorie pour le moment.");
    }
  };

  const handleIdentityContinue = async (validatedAnon = null) => {
    if (!pendingQuickCategory) {
      setIsIdentityModalOpen(false);
      return;
    }

    try {
      const anon = validatedAnon || (await ensureAnon(getGuestPseudo()));
      setPlayer({
        id: anon.uuid,
        pseudo: anon.pseudo,
        isAnon: true,
        guest_id: anon.guest_id,
      });
      setIsIdentityModalOpen(false);
      await launchCategory(pendingQuickCategory, anon);
    } catch (error) {
      console.error(error);
      window.alert("Impossible de lancer la partie apres identification.");
    }
  };

  const visibleTopHeaderLinks = (
    topHeaderLinks.length > 0 ? topHeaderLinks : DEFAULT_TOP_HEADER_LINKS
  ).filter((item) => item?.is_active !== false);

  return (
    <header className="sticky top-0 z-[80] border-b border-emerald-100/90 bg-white text-pixy-ink shadow-[0_12px_36px_rgba(24,48,74,0.08)]">
      {visibleTopHeaderLinks.length > 0 ? (
        <div className="border-b border-emerald-100 bg-pixy-mist/95">
          <div className="mx-auto flex max-w-screen-2xl items-center justify-end gap-1.5 px-4 py-1 sm:px-6 lg:px-8">
            {visibleTopHeaderLinks.map((item) => {
              const content = (
                <>
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-pixy-blue/15 bg-white text-pixy-blue">
                    <SocialIconBadge iconKey={item.icon || item.icon_key} />
                  </span>
                  <span>{item.label}</span>
                </>
              );
              const className =
                "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold text-slate-600 transition hover:bg-white hover:text-pixy-blue";

              return isExternalLink(item.url) ? (
                <a
                  key={item.id || item.url}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={className}
                >
                  {content}
                </a>
              ) : (
                <Link key={item.id || item.url} to={item.url} className={className}>
                  {content}
                </Link>
              );
            })}
          </div>
        </div>
      ) : null}
      <div className="mx-auto flex max-w-screen-2xl items-center justify-between gap-4 px-4 py-1.5 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3">
          <Link
            to="/"
            className="group flex items-center gap-3 rounded-full transition hover:scale-[1.02]"
          >
            <img src={logo} alt="PixyQuiz" className="h-16 w-auto object-contain sm:h-20 lg:h-24" />
            <div className="flex flex-col leading-tight">
              <span className="font-serif text-2xl font-black tracking-tight text-teal-700 drop-shadow-sm sm:text-3xl">
                PixyQuiz
              </span>
              <span className="text-xs italic text-amber-700 sm:text-sm">
                Le Monde en Images
              </span>
            </div>
          </Link>
        </div>

        <nav className="hidden items-center gap-2 lg:flex">
          {NAV_ITEMS_BEFORE_COMMUNITY.map((item) => (
            <Link
              key={item.label}
              to={item.href}
              className="rounded-full px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-pixy-sky hover:text-pixy-blue"
            >
              {item.label}
            </Link>
          ))}
          {isAuthenticated ? (
            <Link
              to="/suggestions-des-joueurs/"
              className="rounded-full px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-pixy-sky hover:text-pixy-blue"
            >
              Photothèque
            </Link>
          ) : null}
          {NAV_ITEMS_AFTER_COMMUNITY.map((item) => (
            <Link
              key={item.label}
              to={item.href}
              className="rounded-full px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-pixy-sky hover:text-pixy-blue"
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={() => setIsQuickPlayOpen(true)}
            className="rounded-full bg-gradient-to-r from-pixy-green via-pixy-blue to-sky-500 px-4 py-2.5 text-xs font-bold uppercase tracking-[0.24em] text-white shadow-lg shadow-sky-400/25 transition hover:-translate-y-0.5 hover:shadow-sky-400/40 sm:px-5 sm:text-sm"
          >
            Jouer
          </button>

          {canAccessMemberArea ? (
            <div className="relative hidden sm:block" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setProfileOpen((current) => !current)}
                className="flex items-center gap-3 rounded-full border border-emerald-100 bg-white px-2 py-1.5 shadow-sm transition hover:border-pixy-green/40 hover:bg-pixy-mist"
              >
                <Avatar
                  src={currentAvatar?.image || user?.avatar || null}
                  name={user?.username || "Profil"}
                  size="sm"
                  className="rounded-full border-pixy-green/30"
                />
                <div className="hidden text-left lg:block">
                  <div className="text-xs uppercase tracking-[0.24em] text-slate-400">
                    Joueur
                  </div>
                  <div className="max-w-[140px] truncate text-sm font-semibold text-pixy-ink">
                    {user?.username || "Profil"}
                  </div>
                </div>
              </button>

              {profileOpen && (
                <div className="absolute right-0 z-50 mt-3 w-56 overflow-hidden rounded-2xl border border-emerald-100 bg-white py-2 shadow-2xl">
                  <Link
                    to="/profile"
                    className="block px-4 py-3 text-sm text-slate-700 transition hover:bg-pixy-mist"
                    onClick={() => setProfileOpen(false)}
                  >
                    Mon espace joueur
                  </Link>
                  <Link
                    to={`/joueur/u/${user?.user_id || user?.id}`}
                    className="block px-4 py-3 text-sm text-slate-700 transition hover:bg-pixy-mist"
                    onClick={() => setProfileOpen(false)}
                  >
                    Mon profil public
                  </Link>
                  <Link
                    to="/notifications"
                    className="flex items-center justify-between gap-3 px-4 py-3 text-sm text-slate-700 transition hover:bg-pixy-mist"
                    onClick={() => setProfileOpen(false)}
                  >
                    <span>Notifications</span>
                    {unreadNotifications > 0 ? (
                      <span className="rounded-full bg-pixy-yellow px-2 py-0.5 text-xs font-black text-pixy-ink">
                        {unreadNotifications}
                      </span>
                    ) : null}
                  </Link>
                  <Link
                    to="/profile/image-proposals"
                    className="block px-4 py-3 text-sm text-slate-700 transition hover:bg-pixy-mist"
                    onClick={() => setProfileOpen(false)}
                  >
                    Mes propositions
                  </Link>
                  <Link
                    to="/profile/premium-social"
                    className="block px-4 py-3 text-sm text-slate-700 transition hover:bg-pixy-mist"
                    onClick={() => setProfileOpen(false)}
                  >
                    Mes amis
                  </Link>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full px-4 py-3 text-left text-sm text-slate-700 transition hover:bg-pixy-mist"
                  >
                    Déconnexion
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="hidden items-center gap-2 lg:flex">
              <button
                type="button"
                onClick={() => handleAuthClick("connexion")}
                className="rounded-full border border-pixy-blue/20 bg-white px-4 py-2 text-sm font-semibold text-pixy-blue shadow-sm transition hover:bg-pixy-sky"
              >
                Connexion / Inscription
              </button>
            </div>
          )}

          <button
            type="button"
            onClick={() => setIsMobileMenuOpen((current) => !current)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-emerald-100 bg-white text-xl text-pixy-ink shadow-sm transition hover:bg-pixy-mist lg:hidden"
            aria-label="Ouvrir le menu"
          >
            {isMobileMenuOpen ? "×" : "≡"}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="border-t border-emerald-100 bg-white/96 px-4 py-4 lg:hidden">
          <div className="flex flex-col gap-2">
            {NAV_ITEMS_BEFORE_COMMUNITY.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                className="rounded-2xl px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-pixy-mist"
              >
                {item.label}
              </Link>
            ))}
            {isAuthenticated ? (
              <Link
                to="/suggestions-des-joueurs/"
                className="rounded-2xl px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-pixy-mist"
              >
                Photothèque
              </Link>
            ) : null}
            {NAV_ITEMS_AFTER_COMMUNITY.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                className="rounded-2xl px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-pixy-mist"
              >
                {item.label}
              </Link>
            ))}
            {canAccessMemberArea ? (
              <>
                <Link
                  to="/profile"
                  className="rounded-2xl px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-pixy-mist"
                >
                  Mon espace joueur
                </Link>
                <Link
                  to={`/joueur/u/${user?.user_id || user?.id}`}
                  className="rounded-2xl px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-pixy-mist"
                >
                  Mon profil public
                </Link>
                <Link
                  to="/notifications"
                  className="flex items-center justify-between gap-3 rounded-2xl px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-pixy-mist"
                >
                  <span>Notifications</span>
                  {unreadNotifications > 0 ? (
                    <span className="rounded-full bg-pixy-yellow px-2 py-0.5 text-xs font-black text-pixy-ink">
                      {unreadNotifications}
                    </span>
                  ) : null}
                </Link>
                <Link
                  to="/profile/image-proposals"
                  className="rounded-2xl px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-pixy-mist"
                >
                  Mes propositions
                </Link>
                <Link
                  to="/profile/premium-social"
                  className="rounded-2xl px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-pixy-mist"
                >
                  Mes amis
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="rounded-2xl px-4 py-3 text-left text-sm font-semibold text-slate-700 transition hover:bg-pixy-mist"
                >
                  Déconnexion
                </button>
              </>
            ) : null}
            {!canAccessMemberArea && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => handleAuthClick("connexion")}
                  className="w-full rounded-2xl border border-pixy-blue/20 bg-pixy-sky px-4 py-3 text-sm font-semibold text-pixy-blue"
                >
                  Connexion / Inscription
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <QuickPlayModal
        isOpen={isQuickPlayOpen}
        onClose={() => setIsQuickPlayOpen(false)}
        onSelectCategory={handleQuickPlaySelect}
      />

      <IdentityChoiceModal
        isOpen={isIdentityModalOpen}
        onClose={() => {
          setIsIdentityModalOpen(false);
          setPendingQuickCategory(null);
        }}
        onContinue={handleIdentityContinue}
      />
    </header>
  );
}

export default Header;